import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Toast from '../components/Toast';

function Settings() {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [notificationData, setNotificationData] = useState({
    emailOnError: false,
    emailSchedule: null
  });
  const [maxContacts, setMaxContacts] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user) {
      setNotificationData({
        emailOnError: user.email_on_error || false,
        emailSchedule: user.email_schedule || null
      });
      setMaxContacts(user.max_contacts || 0);
    }
  }, [user]);

  const handleSaveMaxContacts = async () => {
    setContactsLoading(true);
    setToast(null);
    try {
      await axios.put("/api/dashboard/profile", { maxContacts: maxContacts });
      setToast({ message: "Contact limit saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.error || "Failed to save contact limit", type: "error" });
    } finally {
      setContactsLoading(false);
    }
  };

  const handleNotificationChange = (field, value) => {
    setNotificationData({
      ...notificationData,
      [field]: value
    });
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    setNotificationLoading(true);

    try {
      await axios.put('/api/dashboard/profile', notificationData);
      setToast({ message: 'Notification preferences updated successfully', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update notification preferences', type: 'error' });
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    setToast(null);

    try {
      await axios.post('/api/dashboard/test-email');
      setToast({ message: 'Test email sent successfully! Check your inbox.', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to send test email', type: 'error' });
    } finally {
      setTestEmailLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="main-content">
      <div className="content-body">
        <div className="page-header">
          <h2>Settings</h2>
          <p style={{ color: "#6b7280", fontSize: "16px" }}>Manage your preferences and notifications</p>
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Display Preferences */}
        <div className="card">
          <div className="card-header">
            <h3>Display Preferences</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '0' }}>
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  style={{ marginRight: '10px', width: 'auto' }}
                />
                <span style={{ fontWeight: '500' }}>Enable Dark Mode</span>
              </label>
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
                Switch to a darker color scheme for comfortable viewing in low-light environments
              </small>
            </div>
          </div>
        </div>

        {/* Contact Limit */}
        <div className="card">
          <div className="card-header">
            <h3>Contact Limit</h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "15px" }}>
              Set your maximum contact limit to track usage against your SFMC license.
            </p>
            <div className="form-group">
              <label>Maximum Contacts</label>
              <input
                type="number"
                value={maxContacts}
                onChange={(e) => setMaxContacts(parseInt(e.target.value) || 0)}
                min="0"
                step="1000"
                style={{ maxWidth: "200px" }}
              />
              <small style={{ color: "#6b7280", fontSize: "12px", display: "block", marginTop: "5px" }}>
                Enter your SFMC contact license limit (e.g., 100000)
              </small>
            </div>
            <button onClick={handleSaveMaxContacts} className="btn btn-primary" disabled={contactsLoading}>
              {contactsLoading ? "Saving..." : "Save Contact Limit"}
            </button>
          </div>
        </div>

        {/* Email Notifications */}
        <div className="card">
          <div className="card-header">
            <h3>Email Notifications</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleNotificationSubmit}>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '0' }}>
                  <input
                    type="checkbox"
                    checked={notificationData.emailOnError}
                    onChange={(e) => handleNotificationChange('emailOnError', e.target.checked)}
                    style={{ marginRight: '10px', width: 'auto' }}
                  />
                  <span style={{ fontWeight: '500' }}>Send email alerts for monitoring errors</span>
                </label>
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '5px', marginLeft: '30px' }}>
                  Receive immediate email notifications when monitoring checks fail
                </small>
              </div>

              <div className="form-group">
                <label>Daily Summary Email</label>
                <select
                  value={notificationData.emailSchedule || ''}
                  onChange={(e) => handleNotificationChange('emailSchedule', e.target.value || null)}
                  className="form-control"
                >
                  <option value="">Disabled</option>
                  <option value="08:00">8:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="17:00">5:00 PM</option>
                  <option value="20:00">8:00 PM</option>
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Receive a daily summary of all monitoring activity at your chosen time
                </small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={notificationLoading}>
                {notificationLoading ? 'Saving...' : 'Save Preferences'}
              </button>
            </form>

            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ marginBottom: '10px', fontSize: '16px' }}>Test Email Notifications</h4>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
                Send a test email to verify your notification settings are working correctly.
              </p>
              <button
                onClick={handleTestEmail}
                className="btn btn-secondary"
                disabled={testEmailLoading}
              >
                {testEmailLoading ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '2px solid #dc2626' }}>
          <h3 style={{ color: '#dc2626', marginBottom: '10px' }}>Danger Zone</h3>
          <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '15px' }}>
            Once you sign out, you'll need to log in again to access your account.
          </p>
          <button
            onClick={logout}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
